import type { UseAppForm } from './form';

import { useStore } from '@tanstack/react-form';
import { use, useCallback, useMemo } from 'react';
import { batch } from '@tanstack/store';
import deepEqual from 'deep-equal';

import ContentObjectFormContext from '../contexts/ContentObjectFormContext';
import WebsocketContext from '../contexts/WebsocketContext';

import { syncCollaborativeFieldValue } from './collaboration/syncYjs';
import { useFormContext } from './form';

const getRootFieldName = (fieldName: string) =>
  fieldName.split(/[.[\]]/).filter(Boolean)[0];

/**
 * Limited information from form context.
 *
 * @memberof FlotiqPlugins.FormApi
 */
export type FormApi = {
  /** True when any field differs from its default value. */
  dirty: boolean;
  /** Indicates whether all current validation rules pass. */
  isValid: boolean;
  /** Returns a snapshot of all form values. */
  getValues: () => unknown;
  /** Returns a map of validation errors keyed by field name. */
  getErrors: () => unknown;
  /** Returns the validation errors for the provided field. */
  getError: (fieldName: string) => unknown;
  /** Returns names of fields marked as dirty. */
  getDirtyFields: () => string[];
  /** Reads the current value of the provided field. */
  getValue: (fieldName: string) => unknown;
  /** Triggers form submission using the current field values. */
  submitForm: {
    (): Promise<void>;
    (submitMeta: unknown): Promise<void>;
  };
  /** Runs validation for all fields and resolves with validation issues. */
  validateForm: (
    cause: 'change' | 'blur' | 'submit' | 'mount' | 'server' | 'dynamic',
  ) => Promise<unknown[]>;
  /** Resets the form values and optionally preserves default values. */
  resetForm: (
    values?: unknown,
    opts?: {
      keepDefaultValues?: boolean;
    },
  ) => void;
  /** Updates a single field value and meta information. */
  setFieldValue: (fieldName: string, value: unknown) => void;
  /** Marks a field as touched or untouched. */
  setFieldTouched: (fieldName: string, touched: boolean) => void;
  /** Replaces multiple field values at once. */
  setValues: (newValues: Record<string, any>) => void;
  /** Marks multiple fields as touched. */
  setTouched: (touchedFields: Record<string, boolean>) => void;
  /** Forces the form to re-render without changing state. */
  rerenderForm: () => void;
  /** Indicates whether the form is currently submitting. */
  isSubmitting: boolean;
};

/**
 * Hook to use form instance for plugins. It provides a subset of form API.
 */
export const useFormApi = () => {
  const form = useFormContext() as unknown as UseAppForm;
  const { yjsDocument } = use(WebsocketContext);
  const { contentType } = use(ContentObjectFormContext);

  const rerenderForm = useCallback(() => {
    form.baseStore.setState((old) => ({
      ...old,
      counter: ((old as { counter?: number }).counter || 0) + 1,
    }));
  }, [form.baseStore]);

  const formState = useStore(form.store, (state) => ({
    dirty: state.isDirty,
    touched: state.isTouched,
    isValid: state.isValid,
    isSubmitting: state.isSubmitting,
    //@ts-expect-error - We manually added counter to store state to force rerender
    counter: state.counter,
  }));

  const getErrors = useCallback(() => form.state.errors?.[0] ?? {}, [form]);

  const getValues = useCallback(() => {
    return form.state.values;
  }, [form]);

  const getError = useCallback(
    (fieldName: string) => form.getFieldMeta(fieldName)?.errors?.[0],
    [form],
  );

  const getDirtyFields = useCallback(() => {
    const dirtyFieldsFromMeta = Object.keys(form.state.fieldMeta || {})
      .filter((fieldName) => form.state.fieldMeta[fieldName]?.isDirty)
      .map(getRootFieldName);

    if (dirtyFieldsFromMeta.length) {
      return [...new Set(dirtyFieldsFromMeta)];
    }

    const values = (form.state.values || {}) as Record<string, unknown>;
    const defaultValues = (form.options.defaultValues || {}) as Record<
      string,
      unknown
    >;

    return Object.keys(values).filter(
      (fieldName) => !deepEqual(values[fieldName], defaultValues[fieldName]),
    );
  }, [form]);

  const setFieldTouched = useCallback(
    (fieldName: string, touched: boolean) => {
      form.setFieldMeta(fieldName, (prev) => ({
        ...prev,
        isTouched: touched,
      }));
    },
    [form],
  );

  const setTouched = useCallback(
    (touchedFields: Record<string, boolean>) => {
      batch(() => {
        Object.keys(touchedFields).forEach((key) => {
          setFieldTouched(key, touchedFields[key]);
        });
      });
    },
    [setFieldTouched],
  );

  const setFieldValue = useCallback(
    (fieldName: string, value: unknown) => {
      form.setFieldValue(fieldName, value);

      if (!yjsDocument || !contentType) return;
      syncCollaborativeFieldValue(contentType, fieldName, value, yjsDocument);
    },
    [contentType, form, yjsDocument],
  );

  const setValues = useCallback(
    (newValues: Record<string, any>) => {
      const updateFields = () =>
        Object.keys(newValues).forEach((key) => {
          setFieldValue(key, newValues[key]);
        });

      if (yjsDocument) {
        yjsDocument.transact(() => {
          updateFields();
        });
      } else {
        updateFields();
      }
    },
    [setFieldValue, yjsDocument],
  );

  const formProps = useMemo(
    () => ({
      dirty: formState.dirty,
      getErrors,
      getError,
      getDirtyFields,
      getValues,
      getValue: form.getFieldValue,
      isValid: formState.isValid,
      submitForm: form.handleSubmit,
      validateForm: form.validateAllFields,
      resetForm: form.reset,
      setFieldValue: setFieldValue,
      setFieldTouched,
      setValues,
      setTouched,
      rerenderForm,
      isSubmitting: formState.isSubmitting,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      formState.dirty,
      formState.isValid,
      getErrors,
      getError,
      getDirtyFields,
      getValues,
      form.handleSubmit,
      form.validateAllFields,
      form.reset,
      form.getFieldValue,
      setFieldValue,
      setFieldTouched,
      setValues,
      setTouched,
      rerenderForm,
      formState.isSubmitting,
      formState.counter, // to rerender when counter changes
    ],
  );

  return formProps;
};
